import { useResizeObserver } from '@folklore/hooks';
import { animated } from '@react-spring/web';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import React, { useMemo, ForwardedRef, forwardRef } from 'react';

import { getPrefixedProps, getTrackingQuery } from '../../lib/utils';

import styles from '../../styles/lists/list.module.css';

export type ListItem = Record<string, unknown> | null;

export interface ListCardProps extends CardProps {
    cardComponent?: React.ElementType;
}

export interface ListProps<T = ListItem> {
    items?: T[] | null;
    recommendationId?: string | null;
    clickRef?: string | null;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    cardComponent: React.ElementType;
    cardTheme?: string | null;
    cardSize?: string | null;
    cardWidth?: number | null;
    cardRatio?: string | null;
    cardResponsive?: boolean;
    cardImageLoading?: string | null;
    cardImageLazyLoadingStartIndex?: number | null;
    cardWith?: string | string[] | null;
    cardWithout?: string | string[] | null;
    cardProps?: ListCardProps | ((item: T, index: number) => ListCardProps) | null;
    cardLinkQuery?: Record<string, unknown> | null;
    withBorder?: boolean;
    className?: string | null;
    itemsClassName?: string | null;
    itemsStyle?: React.CSSProperties | null;
    itemClassName?: string | null;
    itemStyle?: React.CSSProperties | ((item: T, index: number) => React.CSSProperties) | null;
    cardClassName?: string | null;
    thumbnailClassName?: string | null;
    onClickCard?: ((e: React.MouseEvent, item: T, index: number) => void) | null;
    itemsRef?: React.Ref<HTMLDivElement>;
}

function List<T = ListItem>(
    {
        items = null,
        recommendationId = null,
        clickRef = null,
        header = null,
        footer = null,
        cardComponent: CardComponent,
        cardTheme = null,
        cardSize = null,
        cardWidth = null,
        cardRatio = null,
        cardResponsive = false,
        cardImageLoading = null,
        cardImageLazyLoadingStartIndex = null,
        cardWith = null,
        cardWithout = null,
        cardProps = null,
        cardLinkQuery = null,
        withBorder = false,
        className = null,
        itemsClassName = null,
        itemsStyle = null,
        itemClassName = null,
        itemStyle = null,
        cardClassName = null,
        thumbnailClassName = null,
        onClickCard = null,
        itemsRef = null,
    }: ListProps<T>,
    containerRef: ForwardedRef<HTMLDivElement>,
) {
    const {
        ref: firstCardResizeRef,
        entry: { contentRect: firstCardContentRect = null },
    } = useResizeObserver({
        disabled: cardWidth !== null,
    });
    const { width: firstCardWidth = null } = firstCardContentRect || {};

    const finalLinkQuery = useMemo(
        () =>
            cardLinkQuery !== null || recommendationId !== null || clickRef !== null
                ? {
                      ...cardLinkQuery,
                      ...getTrackingQuery({
                          recommendationId,
                          clickRef,
                      }),
                  }
                : null,
        [cardLinkQuery, recommendationId, clickRef],
    );

    if (items === null || items.length === 0) {
        return null;
    }

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            ref={containerRef}
        >
            {header}
            <animated.div
                className={classNames([
                    styles.items,
                    {
                        [itemsClassName]: itemsClassName !== null,
                    },
                ])}
                style={itemsStyle}
                ref={itemsRef}
            >
                {(items || []).map((it, index) => {
                    const { cardComponent: CustomCardComponent = null, ...finalCardProps } =
                        (isFunction(cardProps) ? cardProps(it, index) : cardProps) || {};
                    const FinalCardComponent = CustomCardComponent || CardComponent;
                    return (
                        <animated.div
                            className={classNames([
                                styles.item,
                                {
                                    [itemClassName]: itemClassName !== null,
                                },
                            ])}
                            key={`item-${index}`}
                            style={isFunction(itemStyle) ? itemStyle(it, index) : itemStyle}
                            ref={index === 0 ? firstCardResizeRef : null}
                        >
                            <FinalCardComponent
                                theme={cardTheme}
                                size={cardSize}
                                ratio={cardRatio}
                                responsive={cardResponsive}
                                width={cardWidth || firstCardWidth}
                                withBorder={withBorder}
                                placeholder={it === null}
                                onClick={
                                    onClickCard !== null ? (e) => onClickCard(e, it, index) : null
                                }
                                linkQuery={finalLinkQuery}
                                {...it}
                                {...getPrefixedProps(cardWith, 'with')}
                                {...getPrefixedProps(cardWithout, 'without')}
                                {...finalCardProps}
                                imageLoading={
                                    cardImageLoading ||
                                    (cardImageLazyLoadingStartIndex !== null &&
                                    index >= cardImageLazyLoadingStartIndex
                                        ? 'lazy'
                                        : null)
                                }
                                className={classNames([
                                    styles.card,
                                    {
                                        [cardClassName]: cardClassName !== null,
                                    },
                                ])}
                                thumbnailClassName={thumbnailClassName}
                            />
                        </animated.div>
                    );
                })}
            </animated.div>
            {footer}
        </div>
    );
}

export default forwardRef(List) as <T = ListItem>(
    p: ListProps<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
